import { ChangeEvent, FC, FormEvent, useEffect, useState } from 'react';

// midllewares
import {
  FieldsDuplicationOutputData,
  LinkConfigurationDataOutputData,
  LinkConfigurationsOutputData,
  OpenInputType,
  PaymentRequestOutputData,
} from '../../../../middlewares/Open/openTypes';

// hooks
import useColors from '../../../../hooks/useColors';

// components
import Button from '../../../shared/Button';
import CustomInput from '../../common/CustomInput';
import DuplicateFieldsButton from '../Configurations/MerchantConfigurations/DuplicateFieldsButton';
import LinkConfigurationsInputField from '../Configurations/LinkConfigurationInputField';
import MerchantConfigurationsInputField from '../Configurations/MerchantConfigurationsInputField';

// utils
import isEmail from '../../../../utils/isEmail';
import allowedValidations from '../../../../utils/templateConfigurations/open/allowedValidations';

// icons
import { ReactComponent as AlertIcon } from '../../../../assets/feather/alert-octagon.svg';
import { ReactComponent as TrashIcon } from '../../../../assets/feather/trash-2.svg';
import DuplicatePaymentInputField from '../Configurations/MerchantConfigurations/DuplicatePaymentInputField';

interface BaseFieldsProps {
  paymentRequest: PaymentRequestOutputData;
  onChangeInput: (
    name: string,
    value: string | number,
    displayInputs?: string | number | null
  ) => void;
  inputs: OpenInputType;
  displayInputs: OpenInputType;
  dismissedFields: { [key: string]: boolean };
  onDismissField: (name: string, dismiss: boolean) => void;
  handleSubmit: () => void;
  storeInputsOnLocal: () => void;
  storable: boolean;
  recheckValidations: boolean;
  setRecheckValidations: React.Dispatch<React.SetStateAction<boolean>>;
  // fields duplication
  fieldsDuplication: FieldsDuplicationOutputData;
  duplicatedFields: LinkConfigurationsOutputData[][];
  handleOpenFieldsDuplication: () => void;
  handleDuplicatePaymentInputFieldChange: (value: number) => void;
  dismissDuplicatedFields: (idx: number) => void;
}

const BaseFields: FC<BaseFieldsProps> = (props) => {
  const {
    paymentRequest,
    inputs,
    onChangeInput,
    displayInputs,
    dismissedFields,
    onDismissField,
    handleSubmit,
    storeInputsOnLocal,
    storable,
    recheckValidations,
    setRecheckValidations,
    fieldsDuplication,
    duplicatedFields,
    handleOpenFieldsDuplication,
    handleDuplicatePaymentInputFieldChange,
    dismissDuplicatedFields,
  } = props;
  const { primaryColor, secondaryColor } = useColors();
  const [errors, setErrors] = useState<{ [key: string]: null | string }>(
    paymentRequest.linkConfigurations
      .map((lc) => lc.name)
      .reduce((prev, curr) => ({ ...prev, [curr]: null }), {})
  );
  const [storeInputsCheck, setStoreInputsCheck] = useState<boolean>(false);
  const inputsToValidate = paymentRequest.linkConfigurations
    .filter((lc) => {
      const fieldValidations =
        allowedValidations[
          (lc.customFieldType || '') as keyof typeof allowedValidations
        ] || {};
      const validations = Object.keys(fieldValidations).filter(
        (validation) =>
          validation in ((lc.data as LinkConfigurationDataOutputData) || {})
      );
      return validations.length > 0;
    })
    .map((lc) => lc.name);

  useEffect(() => {
    if (!recheckValidations) return;
    Object.entries(inputs).forEach((val) => {
      const [original, parsed] = validateField(val[1] as string, val[0]);
      onChangeInput(val[0], original, parsed);
    });
    Object.entries(displayInputs).forEach((val) =>
      validateField(val[1] as string, val[0])
    );

    setRecheckValidations(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recheckValidations]);

  const validateField = (value: string, name: string) => {
    let val = value;
    let parsedValue = value;
    let hasError = false;
    if (!inputsToValidate.includes(name)) {
      return [val, parsedValue];
    }
    const lc = paymentRequest.linkConfigurations.filter(
      (linkConf) => linkConf.name === name
    )[0];

    const fieldValidators =
      allowedValidations[lc.customFieldType as keyof typeof allowedValidations];

    for (const validator in fieldValidators) {
      if ((lc.data || {})[validator as keyof typeof lc.data]) {
        [val, parsedValue, hasError] =
          fieldValidators[validator].validator(parsedValue);
      }
      setErrors((values) => ({
        ...values,
        [name]: hasError ? fieldValidators[validator].message : null,
      }));
    }
    return [val, parsedValue];
  };

  const handleChange = (e: ChangeEvent<HTMLFormElement>) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      onChangeInput(name, parseInt(value as string));
    }
    const [val, parsedValue] = validateField(value, name);
    onChangeInput(name, val, parsedValue);
  };

  const handleRemoteChange = (name: string, value: string | number) => {
    const [val, parsedValue] = validateField(`${value}`, name);
    onChangeInput(name, val, parsedValue);
  };

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (storable && storeInputsCheck) storeInputsOnLocal();
    handleSubmit();
  };

  const handleCheck = () => {
    if (!storable) return;
    setStoreInputsCheck(!storeInputsCheck);
  };

  const handleDuplicatedFieldsDeleteClick = (
    e: FormEvent<HTMLButtonElement>,
    idx: number
  ) => {
    e.preventDefault();
    dismissDuplicatedFields(idx);
  };

  return (
    <div className="w-full h-full">
      <form
        onChange={handleChange}
        onSubmit={handleFormSubmit}
        className="h-full flex flex-col"
      >
        {paymentRequest.headerText && (
          <div className="mx-auto mt-1 mb-2 w-full text-left">
            <p className="text-md font-medium w-11/12 mx-auto lg:mx-0">
              <span className="font-bold">Consentimiento: </span>
              {paymentRequest.headerText}
            </p>
          </div>
        )}
        <div
          id="fields-div"
          className="w-full grid gap-4 lg:gap-x-10 gap-y-4 mb-6 lg:grid-cols-2"
        >
          <div className="mx-auto w-11/12 text-left">
            <label
              htmlFor="name"
              className="text-sm font-semibold w-full mr-auto"
              style={{ color: secondaryColor }}
            >
              Nombre:
            </label>
            <CustomInput
              id="name"
              name="name"
              placeholder="Ingrese su nombre"
              value={displayInputs.name}
              disabled={paymentRequest.name !== ''}
            />
          </div>
          <div className="mx-auto w-11/12 text-left">
            <label
              htmlFor="email"
              className="text-sm font-semibold w-full mr-auto"
              style={{ color: secondaryColor }}
            >
              Email:
            </label>
            <CustomInput
              required
              id="email"
              name="email"
              placeholder="Ingrese su email"
              value={displayInputs.email}
            />
            {inputs.email && !isEmail(inputs.email) && (
              <div className="flex text-red-500">
                <AlertIcon className="h-4 my-auto" />
                <p className="text-sm">El mail indicado no es válido</p>
              </div>
            )}
          </div>
          <div className="mx-auto w-11/12 text-left">
            <label
              htmlFor="amount"
              className="text-sm font-semibold w-full mr-auto"
              style={{ color: secondaryColor }}
            >
              {fieldsDuplication.duplicate
                ? 'Monto unitario a pagar:'
                : 'Monto a pagar:'}
            </label>
            <CustomInput
              type="number"
              id="amount"
              name="amount"
              placeholder="Ingrese el monto"
              autoComplete="off"
              value={displayInputs.amount}
              min={1}
              max={100000000000}
              disabled={
                paymentRequest.amount !== 0 && paymentRequest.amount !== null
              }
              icon={<p>$</p>}
              currency={
                paymentRequest.currency ? (
                  <p>{paymentRequest.currency?.toUpperCase()}</p>
                ) : null
              }
            />
            {inputs.amount && (inputs.amount as number) <= 0 && (
              <div className="flex text-red-500">
                <AlertIcon className="h-4 my-auto" />
                <p className="text-sm">El monto debe ser mayor a 0</p>
              </div>
            )}
          </div>
          {paymentRequest.linkConfigurations.map((lc) => (
            <LinkConfigurationsInputField
              key={lc.name}
              linkConfiguration={lc}
              displayInputs={displayInputs}
              dismissedFields={dismissedFields}
              onDismissField={onDismissField}
              errors={errors}
              inputs={inputs}
              handleRemoteChange={handleRemoteChange}
            />
          ))}
        </div>
        <div
          id="fields-div"
          className="w-full grid gap-4 lg:gap-x-10 gap-y-4 mb-6 lg:grid-cols-2"
        >
          {paymentRequest.merchantConfigurations.map((mc) => (
            <MerchantConfigurationsInputField
              key={mc.name}
              merchantConfiguration={mc}
              displayInputs={displayInputs}
              dismissedFields={dismissedFields}
              onDismissField={onDismissField}
              errors={errors}
            />
          ))}
        </div>
        {/* fields duplication */}
        {fieldsDuplication.duplicate && (
          <>
            {duplicatedFields.map((duplicatedConfigurationsFields, idx) => (
              <div
                id="fields-duplication-div"
                className="w-full grid gap-4 lg:gap-x-10 gap-y-4 mb-6 lg:grid-cols-2 border-2 rounded-lg py-4 px-2"
                style={{ borderColor: primaryColor }}
                key={idx}
              >
                <div className="w-full col-span-2 ">
                  <button
                    className="my-auto"
                    onClick={(e) => handleDuplicatedFieldsDeleteClick(e, idx)}
                  >
                    <span className="max-h-2 my-auto text-stone-500 text-sm display-flex flex">
                      <TrashIcon className="max-h-4" /> Eliminar
                    </span>
                  </button>
                </div>
                {duplicatedConfigurationsFields.map((lc) => (
                  <LinkConfigurationsInputField
                    key={lc.name}
                    linkConfiguration={lc}
                    displayInputs={displayInputs}
                    dismissedFields={dismissedFields}
                    onDismissField={onDismissField}
                    errors={errors}
                    inputs={inputs}
                    handleRemoteChange={handleRemoteChange}
                  />
                ))}
              </div>
            ))}
          </>
        )}
        <div
          id="button-div"
          className="mt-auto w-full flex flex-col justify-center items-center lg:items-end lg:mb-0 lg:w-auto lg:pr-4 lg:justify-end"
        >
          {fieldsDuplication.duplicate && (
            <div className="flex mb-5 items-center">
              <div className="flex items-center h-5">
                <DuplicateFieldsButton
                  displayLabel={fieldsDuplication.displayName || ''}
                  type="button"
                  value="duplicate-fields"
                  disabled={
                    fieldsDuplication.maxDuplication <= duplicatedFields.length
                  }
                  onClick={handleOpenFieldsDuplication}
                />
              </div>
            </div>
          )}
          {fieldsDuplication.paymentDuplication.allow &&
            fieldsDuplication.paymentDuplication.maxDuplication > 0 && (
              <div className="flex mb-10 items-center w-1/2 sm:w-full">
                <div className="flex items-center h-5 w-full">
                  <DuplicatePaymentInputField
                    onChange={handleDuplicatePaymentInputFieldChange}
                    value={displayInputs.paymentDuplication}
                    disabled={false}
                    maxDuplication={
                      fieldsDuplication.paymentDuplication.maxDuplication
                    }
                  />
                </div>
              </div>
            )}
          {storable && (
            <div className="flex mb-5 items-center">
              <div className="flex items-center h-5">
                <input
                  id="helper-checkbox"
                  aria-describedby="helper-checkbox-text"
                  type="checkbox"
                  onClick={handleCheck}
                  checked={storeInputsCheck}
                  value=""
                  className="w-4 h-4 bg-gray-100 border-gray-300 rounded"
                  style={{
                    accentColor: primaryColor,
                  }}
                />
              </div>
              <div className="ms-2 text-sm">
                <label
                  htmlFor="helper-checkbox"
                  className="font-semibold text-md text-gray-900"
                  style={{ userSelect: 'none' }}
                >
                  Guardar datos para la próxima vez
                </label>
              </div>
            </div>
          )}
          <Button
            type="submit"
            text="Pagar"
            value="pay"
            disabled={
              !(
                inputs.name !== '' &&
                isEmail(inputs.email) &&
                inputs.amount !== '' &&
                (inputs.amount as number) > 0 &&
                paymentRequest.linkConfigurations.reduce(
                  (prev, curr) =>
                    inputs[curr.name] !== '' &&
                    errors[curr.name] === null &&
                    prev,
                  true
                ) &&
                paymentRequest.merchantConfigurations.reduce(
                  (prev, curr) => inputs[curr.name] !== '' && prev,
                  true
                )
              )
            }
            onClick={() => null}
          />
        </div>
      </form>
    </div>
  );
};

export default BaseFields;
